import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, User, Trophy, Target, TrendingUp, Edit } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';

const PlayerDetailPage = () => {
  const { playerId } = useParams();
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchPlayer = async () => {
      try {
        const playerDoc = await getDoc(doc(db, 'players', playerId));
        if (playerDoc.exists()) {
          setPlayer({ id: playerDoc.id, ...playerDoc.data() });
        } else {
          setError('Player not found');
        }
      } catch (err) {
        setError('Failed to load player details');
        console.error('Error fetching player:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPlayer();
  }, [playerId]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="loading">Loading player details...</div>
      </div>
    );
  }

  if (error || !player) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#1C563D' }}>
            {error || 'Player not found'}
          </h2>
          <Link to="/players" className="btn btn-primary">
            Back to Players
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <Link
          to="/players"
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
        >
          <ArrowLeft size={20} />
          Back to Players
        </Link>
        
        <div className="flex justify-between items-start">
          <div className="flex items-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mr-4 overflow-hidden">
              {player.profileImage ? (
                <img
                  src={player.profileImage}
                  alt={`${player.name} profile`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-green-100 flex items-center justify-center">
                  <User size={32} style={{ color: '#1C563D' }} />
                </div>
              )}
            </div>
            <div>
              <h1 className="text-3xl font-bold mb-2" style={{ color: '#1C563D' }}>
                {player.name}
              </h1>
              <p className="text-gray-600">
                Player profile and statistics
              </p>
            </div>
          </div>
          <Link
            to={`/players/${playerId}/edit`}
            className="btn btn-primary flex items-center gap-2"
          >
            <Edit size={20} />
            Edit Player
          </Link>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Player Info */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="lg:col-span-1"
        >
          <div className="card">
            <h2 className="text-xl font-semibold mb-4" style={{ color: '#1C563D' }}>
              Player Information
            </h2>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">Team</span>
                <span className="font-medium">{player.teamName || 'No team assigned'}</span>
              </div>
              
              {player.playtomicRating && (
                <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                  <span className="text-gray-600">Playtomic Rating</span>
                  <span className="font-bold text-lg" style={{ color: '#1C563D' }}>
                    {player.playtomicRating}
                  </span>
                </div>
              )}
              
              {player.category && (
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <span className="text-gray-600">Category</span>
                  <span className="font-medium text-green-600">{player.category}</span>
                </div>
              )}
              
              {player.position && (
                <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                  <span className="text-gray-600">Position</span>
                  <span className="font-medium">{player.position}</span>
                </div>
              )}
              
              {player.playingHand && (
                <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg">
                  <span className="text-gray-600">Playing Hand</span>
                  <span className="font-medium">{player.playingHand}</span>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Statistics */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="lg:col-span-2"
        >
          <div className="card">
            <h2 className="text-xl font-semibold mb-4" style={{ color: '#1C563D' }}>
              Statistics
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold" style={{ color: '#1C563D' }}>
                  {player.matchesPlayed || 0}
                </div>
                <div className="text-sm text-gray-600">Matches Played</div>
              </div>
              
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {player.aces || 0}
                </div>
                <div className="text-sm text-gray-600">Aces</div>
              </div>
              
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">
                  {player.kickSmashes || 0}
                </div>
                <div className="text-sm text-gray-600">Kick Smashes</div>
              </div>
              
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold" style={{ color: '#1C563D' }}>
                  {player.akhlaaqRating ? `${player.akhlaaqRating}/5` : 'N/A'}
                </div>
                <div className="text-sm text-gray-600">Akhlaaq Rating</div>
              </div>
            </div>

            {player.gamesPlayed && (
              <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Games Played (Playtomic)</span>
                  <span className="font-bold text-lg" style={{ color: '#1C563D' }}>
                    {player.gamesPlayed}
                  </span>
                </div>
              </div>
            )}

            {player.remark && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Remark</div>
                <div className="font-medium">{player.remark}</div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PlayerDetailPage;
